/**
 * App Layout Component
 *
 * WHY: Composes the authenticated shell from focused navigation and header
 * primitives while preserving route and interaction behavior.
 */

import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useTheme } from '@/hooks/useTheme'
import { useMissionContext } from '@/hooks/useMissionContext'
import { cn } from '@/lib/utils'
import { api } from '@/api'
import { AssistantDrawer } from '@/components/assistant/AssistantDrawer'
import { SessionTimeoutHandler } from '@/components/security/SessionTimeoutHandler'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ShellMobileHeader } from '@/components/layout/shell/ShellMobileHeader'
import { ShellDesktopHeader } from '@/components/layout/shell/ShellDesktopHeader'
import { ShellSidebar } from '@/components/layout/shell/ShellSidebar'
import { useShellState } from '@/components/layout/shell/useShellState'

export function AppLayout() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const { selectedMissionId, setSelectedMissionId } = useMissionContext()
  const [assistantOpen, setAssistantOpen] = useState(false)

  const {
    collapsed,
    isMobile,
    mobileMenuOpen,
    expandedModules,
    setCollapsed,
    setMobileMenuOpen,
    toggleModule,
  } = useShellState(location.pathname)

  const { data: missions = [] } = useQuery({
    queryKey: ['platform', 'missions'],
    queryFn: () => api.platform.missions.list(),
  })

  useEffect(() => {
    if (selectedMissionId || missions.length === 0) {
      return
    }
    const activeMission = missions.find(mission => mission.status === 'active') || missions[0]
    setSelectedMissionId(activeMission.mission_id)
  }, [missions, selectedMissionId, setSelectedMissionId])

  const logoHorizontal =
    theme === 'dark'
      ? '/WORDMARK_SPECTREIQ_HORIZONTAL_WHITE.svg'
      : '/WORDMARK_SPECTREIQ_HORIZONTAL_BLACK.svg'
  const logoIcon =
    theme === 'dark'
      ? '/ampersand_icon_white.svg'
      : '/ampersand_icon_black.svg'

  return (
    <TooltipProvider delayDuration={300}>
      <div className="min-h-screen bg-background">
        <SessionTimeoutHandler />

        {isMobile && (
          <ShellMobileHeader
            mobileMenuOpen={mobileMenuOpen}
            logoHorizontal={logoHorizontal}
            theme={theme}
            onToggleMenu={() => setMobileMenuOpen(!mobileMenuOpen)}
          />
        )}

        {isMobile && mobileMenuOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        <ShellSidebar
          isMobile={isMobile}
          mobileMenuOpen={mobileMenuOpen}
          collapsed={collapsed}
          logoHorizontal={logoHorizontal}
          logoIcon={logoIcon}
          pathname={location.pathname}
          user={user}
          expandedModules={expandedModules}
          onNavigate={navigate}
          onToggleModule={toggleModule}
          onExpandSidebar={() => setCollapsed(false)}
          onCollapseSidebar={() => setCollapsed(true)}
          onLogout={logout}
        />

        <main
          className={cn(
            'min-h-screen transition-all duration-300',
            isMobile ? 'pl-0 pt-14' : collapsed ? 'pl-16' : 'pl-72'
          )}
        >
          {!isMobile && (
            <ShellDesktopHeader
              pathname={location.pathname}
              selectedMissionId={selectedMissionId}
              missions={missions}
              theme={theme}
              onOpenAssistant={() => setAssistantOpen(true)}
              onMissionChange={setSelectedMissionId}
            />
          )}

          <div className="p-4 md:p-6">
            <Outlet />
          </div>
        </main>

        <AssistantDrawer open={assistantOpen} onOpenChange={setAssistantOpen} />
      </div>
    </TooltipProvider>
  )
}
